import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ivrDetailQueryOptions } from "@/server/ivr";
import { IVRForm } from "@/routes/_dashboard/ivr/-components/ivr-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect } from "react";

type SearchParams = {
  showInfo?: boolean;
};

export const Route = createFileRoute("/_dashboard/ivr/$ivrId/")({
  component: IVRDetailComponent,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return {
      showInfo: search.showInfo as boolean | undefined,
    };
  },
});

function IVRDetailComponent() {
  const { ivrId } = Route.useParams();
  const ivrQuery = useQuery(ivrDetailQueryOptions(ivrId));

  const ivr = ivrQuery.data;

  useEffect(() => {
    if (!ivrId) return;
    void ivrQuery.refetch();
  }, [ivrId, ivrQuery.refetch]);

  if (ivrQuery.isLoading) {
    return (
      <section>
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>
              <Skeleton className="h-7 w-32" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-10 w-full" />
            </div>

            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full" />
            </div>
          </CardContent>
        </Card>

        <Card className="mt-5 mb-5">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                <Skeleton className="h-7 w-20" />
              </CardTitle>
              <Skeleton className="h-9 w-16" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg p-4">
              <Skeleton className="h-20 w-full" />
            </div>
          </CardContent>
        </Card>

        {/* Action buttons skeleton */}
        <div className="flex gap-2 sticky bottom-0 border-t p-4 bg-background z-10">
          <Skeleton className="h-10 w-16" />
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-10 w-16" />
        </div>
      </section>
    );
  }

  if (ivrQuery.isError) {
    return <div>Error loading IVR details</div>;
  }

  return <IVRForm ivr={ivr} isEdit={true} />;
}
